import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { HeroSearch } from "./HeroSearch";

type Props = {
  /**
   * CSS selector / ref-less heuristic. We watch `window.scrollY`; once past
   * `threshold` px the pill appears.
   */
  threshold?: number;
};

export function StickyHeroSearch({ threshold = 320 }: Props) {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className={
        "fixed inset-x-0 bottom-5 z-30 flex justify-center pl-4 pr-24 transition-all duration-300 md:bottom-7 md:pr-28 " +
        (visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0")
      }
    >
      {visible && (
      <div className="w-full max-w-xl">
        {open ? (
          <div className="rounded-2xl border border-border bg-white p-4 shadow-[0_24px_60px_-15px_rgb(32_56_112/0.45)]">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Ask us anything
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close search"
                className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-primary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <HeroSearch size="large" autoFocus resultsPosition="above" />
            <p className="mt-3 text-xs text-muted-foreground">
              Search services, insights and people — or press Enter to send your
              question as an enquiry.
            </p>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="group inline-flex items-center gap-3 rounded-full border border-border bg-white px-5 py-3 text-base font-medium text-primary shadow-[0_18px_50px_-15px_rgb(32_56_112/0.35)] backdrop-blur transition hover:border-primary/40 hover:shadow-[0_24px_60px_-15px_rgb(32_56_112/0.45)]"
              aria-label="Open search"
            >
              <Search className="h-4 w-4 text-primary/70 transition group-hover:text-primary" />
              <span className="text-muted-foreground">Ask us anything…</span>
              <kbd className="hidden items-center rounded-md border border-primary/15 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold text-primary/70 sm:inline-flex">
                ⌘K
              </kbd>
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
